// MACHINERY V2 (Stroje) — the document-title field, shared by the upload and
// rename dialogs (M2.5).
//
// Spec: data package §6 `[form] document name` — Editable YES, Required YES,
// `maxLength { value: 50 }`. Artboard 1005 draws it with the live counter
// ("19 / 50") and the corrected helper line.
//
// ── FIX 4 (ticket §7). v1 pairs a 50-character validator with a message that
//    says 100: components/manualsAndSoftware/DocumentFileItem.tsx:82 is
//        maxLength: { value: 50, message: translate("titleTooLong") }
//    and `myMachinery:titleTooLong` reads "Title is too long (100 characters
//    max)" in en and the same in sk. The string is NOT edited, because it is
//    shared: `titleTooLong` is also used with a REAL limit of 100 by
//    CreateServiceReportPage.tsx:485, ServiceReportDialog.tsx:311,
//    servicing/ServiceRequestForm.tsx:219 and
//    servicing/StepMachineAndDetails.tsx:673 (grep-verified — those four are
//    every myMachinery-namespace caller; `public:titleTooLong` and
//    `public/v2/reports/CreateReportV2.tsx` are a different key in a different
//    namespace and are untouched). Changing it to "50" would make those four
//    lie instead. v2 uses a NEW key, `documentTitleTooLong`, so the two limits
//    can finally disagree without either being wrong.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";

/** DocumentFileItem.tsx:82 — the validator v2 keeps and finally tells the
 *  truth about. */
export const DOC_NAME_MAX = 50;

export type DocNameError = "required" | "tooLong" | null;

/** Same order as react-hook-form applies the v1 rules: required, then length. */
export const docNameError = (value: string): DocNameError => {
  const trimmed = value.trim();
  if (!trimmed) return "required";
  if (value.length > DOC_NAME_MAX) return "tooLong";
  return null;
};

export interface DocNameFieldProps {
  value: string;
  onChange: (v: string) => void;
  /** Show the error even before the field is touched (after a submit attempt). */
  showError?: boolean;
  autoFocus?: boolean;
}

const DocNameField: React.FC<DocNameFieldProps> = ({
  value,
  onChange,
  showError,
  autoFocus,
}) => {
  const { translate } = useTranslation("myMachinery");
  const error = showError ? docNameError(value) : null;
  const message =
    error === "required"
      ? translate("common:forms.required")
      : error === "tooLong"
      ? translate("myMachinery:documentTitleTooLong")
      : null;

  return (
    <div style={{ minWidth: 0 }}>
      <div className="eyebrow" style={{ marginBottom: 5 }}>
        {translate("myMachinery:v2.tabs.docs.nameLabel")} *
      </div>
      <div
        style={{
          height: 38,
          padding: "0 12px",
          background: "var(--card)",
          border: "1px solid " + (message ? "var(--red)" : "var(--border)"),
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          gap: 8,
          minWidth: 0,
        }}
      >
        <input
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          aria-label={translate("myMachinery:v2.tabs.docs.nameLabel")}
          aria-invalid={!!message}
          style={{
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: "none",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: 13.5,
            color: "var(--ink)",
          }}
        />
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: value.length > DOC_NAME_MAX ? "var(--red)" : "var(--faint)",
            flexShrink: 0,
          }}
        >
          {value.length} / {DOC_NAME_MAX}
        </span>
      </div>
      <div
        style={{
          fontSize: 11,
          color: message ? "var(--red)" : "var(--faint)",
          marginTop: 5,
        }}
      >
        {message || translate("myMachinery:v2.tabs.docs.nameHint", { max: DOC_NAME_MAX })}
      </div>
    </div>
  );
};

export default DocNameField;
